import React, { Component, ErrorInfo, ReactNode } from 'react';
import i18n from '@/locales';
import ErrorFallback from './ErrorFallback';
import { reloadOnChunkError } from '@/lib/lazyWithRetry';
import { reportErrorDeferred } from '@/lib/report-error-deferred';

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Fallback component to render when an error occurs */
  fallback?: ReactNode;
  /** Callback function called when an error is caught */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  /** Callback function called when retry is clicked */
  onRetry?: () => void;
  /** Custom title for the error message */
  errorTitle?: string;
  /** Custom description for the error message */
  errorDescription?: string;
  /** Whether to show the report error button */
  showReportButton?: boolean;
  /** Whether to show the back to dashboard button */
  showBackButton?: boolean;
  /** Section name for logging purposes */
  sectionName?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * ErrorBoundary component that catches JavaScript errors anywhere in their child
 * component tree, logs those errors, and displays a fallback UI.
 *
 * Usage:
 * ```tsx
 * <ErrorBoundary
 *   sectionName="Dashboard"
 *   onError={(error, info) => console.error(error)}
 * >
 *   <YourComponent />
 * </ErrorBoundary>
 * ```
 */
class ErrorBoundary extends Component<Readonly<ErrorBoundaryProps>, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Stale chunk after deploy: reload the page (throttled) instead of
    // showing the error fallback. This is a safety net in case the lazy()
    // wrapper did not catch the import rejection itself.
    if (reloadOnChunkError(error)) {
      return;
    }

    // Log the error to console
    if (import.meta.env.DEV) {
      console.error('[ErrorBoundary] Error caught:', {
        error: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        sectionName: this.props.sectionName || 'Unknown',
        timestamp: new Date().toISOString(),
      });
    }

    // Update state with error info
    this.setState({ errorInfo });

    // Call the onError callback if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Here you could also send the error to an error reporting service
    // Example: sendToErrorReportingService(error, errorInfo, this.props.sectionName);
    this.logErrorToService(error, errorInfo);
  }

  /**
   * Forwards the caught error to the pluggable error reporter
   * (src/lib/error-reporter.ts), which handles transport, PII scrubbing and
   * dedupe. The reporter never throws. It is loaded on demand
   * (`reportErrorDeferred`) so it stays out of the initial chunk — this
   * boundary wraps the app root and is always in the boot path.
   */
  private logErrorToService(error: Error, errorInfo: ErrorInfo): void {
    void reportErrorDeferred(error, {
      kind: 'react-error-boundary',
      sectionName: this.props.sectionName || 'Unknown',
      componentStack: errorInfo.componentStack ?? undefined,
    });
  }

  /**
   * Resets the error state and attempts to re-render the children
   */
  handleRetry = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });

    // Call the onRetry callback if provided
    if (this.props.onRetry) {
      this.props.onRetry();
    }
  };

  /**
   * Handles error reporting from the user
   */
  handleReportError = (): void => {
    const { error, errorInfo } = this.state;
    const { sectionName } = this.props;

    // Create error report data
    const errorReport = {
      message: error?.message || 'Unknown error',
      stack: error?.stack || '',
      componentStack: errorInfo?.componentStack || '',
      sectionName: sectionName || 'Unknown',
      timestamp: new Date().toISOString(),
      url: window.location.href,
    };

    // In production, this would open a modal or send to a support system
    // For now, we'll just copy to clipboard
    const reportText = `
${i18n.t('errors:boundary.reportHeader')}
=======================
${i18n.t('errors:boundary.reportSection')}: ${errorReport.sectionName}
${i18n.t('errors:boundary.reportTime')}: ${errorReport.timestamp}
${i18n.t('errors:boundary.reportUrl')}: ${errorReport.url}

${i18n.t('errors:boundary.reportError')}: ${errorReport.message}

${i18n.t('errors:boundary.reportStackTrace')}:
${errorReport.stack}

${i18n.t('errors:boundary.reportComponentStack')}:
${errorReport.componentStack}
    `.trim();

    if (navigator.clipboard) {
      navigator.clipboard.writeText(reportText).then(() => {
        alert(i18n.t('errors:boundary.reportCopied'));
      }).catch(() => {
        if (import.meta.env.DEV) console.error('[ErrorBoundary] Failed to copy error report to clipboard');
      });
    }
  };

  render(): ReactNode {
    const {
      hasError,
      error
    } = this.state;

    const {
      children,
      fallback,
      errorTitle,
      errorDescription,
      showReportButton = true,
      showBackButton = true,
    } = this.props;

    if (hasError) {
      // If a custom fallback is provided, use it
      if (fallback) {
        return fallback;
      }

      // Otherwise, use the default ErrorFallback component
      return (
        <ErrorFallback
          error={error}
          title={errorTitle}
          description={errorDescription}
          onRetry={this.handleRetry}
          onReport={showReportButton ? this.handleReportError : undefined}
          showBackButton={showBackButton}
        />
      );
    }

    return children;
  }
}

export default ErrorBoundary;

/**
 * Higher-order component version of ErrorBoundary
 */
export function withErrorBoundary<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  errorBoundaryProps?: Omit<ErrorBoundaryProps, 'children'>
) {
  const displayName = WrappedComponent.displayName || WrappedComponent.name || 'Component';

  const ComponentWithErrorBoundary = (props: Readonly<P>) => (
    <ErrorBoundary {...errorBoundaryProps} sectionName={displayName}>
      <WrappedComponent {...props} />
    </ErrorBoundary>
  );

  ComponentWithErrorBoundary.displayName = `withErrorBoundary(${displayName})`;

  return ComponentWithErrorBoundary;
}
